import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Opacity } from "@faire/web/slate/Color";
import { Row } from "@faire/web/slate/Layout";
import React from "react";
import { useMemo } from "react";
import styled from "styled-components";

const WINDOW_TO_SHOW = [-2, -1, 0, 1, 2, 3, 4];
const SMALLEST_POSSIBLE_INDEX = 0;
const SIZE_OF_DOT_CONTAINER = 8;
const LARGE_SIZE_OF_DOT_CONTAINER = 18;

interface IProps {
  onClick?: (value: number) => void;
  numValidImages: number;
  currentImageIndex: number;
  className?: string;
  dotSize?: "default" | "large";
  /**
   * Whether the dots should be grey/black-on-white, or white-on-black.
   */
  colorVariant?: DotsColorVariant;
}

enum DotSize {
  s = "2px",
  m = "3px",
  l = "4px",
}

enum DotSizeLarge {
  s = "4px",
  m = "5px",
  l = "6px",
}

export type DotsColorVariant = "dark" | "light";

export const CarouselDots: React.FC<IProps> = ({
  currentImageIndex,
  numValidImages,
  className,
  colorVariant,
  onClick,
  ...props
}) => {
  const { strictLocalize } = useStrictLocalization();
  const getDotSize = (index: number) => {
    const dotSizes = dotSize;
    // When we have less than we need for our scrolling window just make everything large
    if (numValidImages <= WINDOW_TO_SHOW.length) {
      return dotSizes.l;
    }
    // This block will keep the dots large when the image is all the way on one side
    // This means we will only have medium and small dots at the end of the line
    if (smallestShowingItem <= SMALLEST_POSSIBLE_INDEX) {
      if (index < WINDOW_TO_SHOW.length - 2) {
        return dotSizes.l;
      } else if (index === WINDOW_TO_SHOW.length - 2) {
        return dotSizes.m;
      }
    } else if (largestShowingItem >= largestPossibleIndex) {
      if (index > numValidImages - (WINDOW_TO_SHOW.length - 1)) {
        return dotSizes.l;
      } else if (index === numValidImages - (WINDOW_TO_SHOW.length - 1)) {
        return dotSizes.m;
      }
    }

    // this.WINDOW_TO_SHOW[3] will get a value of [-1,0,1] depending on where in the window we are
    // this means centralIndex will work as the index we can consider the center
    const centralIndex = currentImageIndex + (WINDOW_TO_SHOW[3] ?? 0);
    if (index < centralIndex - 2 || index > centralIndex + 2) {
      return dotSizes.s;
    } else if (index < centralIndex - 1 || index > centralIndex + 1) {
      return dotSizes.m;
    } else {
      return dotSizes.l;
    }
  };

  const largestPossibleIndex = numValidImages - 1;

  const smallestShowingItem = useMemo(() => {
    // we want to show at all 7 dots, so if there is not enough on the right,
    //  use those on the left
    if (
      (WINDOW_TO_SHOW[WINDOW_TO_SHOW.length - 1] ?? 0) + currentImageIndex >=
      largestPossibleIndex
    ) {
      return numValidImages - WINDOW_TO_SHOW.length;
    } else {
      return Math.max((WINDOW_TO_SHOW[0] ?? 0) + currentImageIndex, 0);
    }
  }, [currentImageIndex, largestPossibleIndex, numValidImages]);

  const largestShowingItem = useMemo(() => {
    // we want to show at all 7 dots, so if there is not enough on the left,
    //  use those on the right
    if (
      (WINDOW_TO_SHOW[0] ?? 0) + currentImageIndex <=
      SMALLEST_POSSIBLE_INDEX
    ) {
      return WINDOW_TO_SHOW.length - 1;
    } else {
      return Math.min(
        (WINDOW_TO_SHOW[WINDOW_TO_SHOW.length - 1] ?? 0) + currentImageIndex,
        numValidImages - 1
      );
    }
  }, [currentImageIndex, numValidImages]);

  const dotContainerSize =
    props.dotSize === "large"
      ? LARGE_SIZE_OF_DOT_CONTAINER
      : SIZE_OF_DOT_CONTAINER;

  const dotSize = props.dotSize === "large" ? DotSizeLarge : DotSize;

  const leftOffsetPx = dotContainerSize * smallestShowingItem;

  return (
    <Wrapper
      className={className}
      $amount={Math.min(numValidImages, WINDOW_TO_SHOW.length)}
      $dotContainerSize={dotContainerSize}
    >
      <StyledRow style={{ transform: `translateX(-${leftOffsetPx}px)` }}>
        {[...new Array(numValidImages).keys()].map((value) => (
          <DotWrapper
            key={value}
            $dotContainerSize={dotContainerSize}
            $height={dotSize.l}
          >
            <Dot
              $active={value === currentImageIndex}
              onClick={() => {
                onClick?.(value);
              }}
              $size={getDotSize(value)}
              $colorVariant={colorVariant}
              // accessibility features
              role="button"
              aria-disabled={value === currentImageIndex}
              aria-label={strictLocalize(
                {
                  defaultMessage:
                    "Show carousel slide {slideNumber} of {totalSlidesNumber}",
                  description: {
                    text: "Aria label for the dots carousel slide button",
                  },
                },
                {
                  slideNumber: value + 1,
                  totalSlidesNumber: numValidImages,
                }
              )}
            />
          </DotWrapper>
        ))}
      </StyledRow>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $amount: number; $dotContainerSize: number }>`
  width: ${(props) => props.$dotContainerSize * props.$amount}px;
  margin: auto;
  overflow: hidden;
`;

const StyledRow = styled(Row)`
  transition: transform 0.25s ease-in;
  & > * {
    flex-shrink: 0;
  }
`;

const DotWrapper = styled.div<{ $dotContainerSize: number; $height: string }>`
  width: ${({ $dotContainerSize }) => $dotContainerSize}px;
  height: ${({ $height }) => $height};
  position: relative;
`;

const Dot = styled.div<{
  $active: boolean;
  $size: DotSize | DotSizeLarge;
  $colorVariant?: DotsColorVariant;
}>`
  width: ${(props) => props.$size};
  height: ${(props) => props.$size};
  transition: background-color 0.25s;
  margin: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background-color: ${(props) => {
    switch (props.$colorVariant) {
      case "light":
        return props.$active
          ? Core.surface.inverse
          : Core.surface.muted + Opacity.O_40;
      case "dark":
      default:
        return props.$active ? Core.border.default : Core.border.muted;
    }
  }};
`;
